// One flight's 3-day championship bracket in the PBCC scheme: cream match
// boxes, crimson connectors/frame, gold section labels on dark green. The
// bracket itself is the priority, so boxes are large and the Day 1/2/3 labels
// live in the card header (not here) to leave the most room. Day 1 = the 4 R1
// matchups; Day 2 = Winners (W1-W4) over Consolation (L1-L4); Day 3 =
// Championship (W5/W6), 3rd (L5/L6), Consolation Final (L7/L8), 5th (L9/L10),
// each row aligned to its Round-1 row. Empty slots show gold W#/L# labels and
// fill with team names (crimson while live) as play advances.
import { useRef, useState, useLayoutEffect } from 'react'
import { FOURBALL_ONLY, PBCC_PREMIUM } from '../config/siteMode.js'
import { lastNames } from '../lib/teamName.js'
// Box fill/text are hard-coded literals here so the DOM is unambiguously opaque.
// TEST/premium: cream boxes + gold labels; club/live: white boxes + white labels.

const CREAM = PBCC_PREMIUM ? 'var(--ge-cream-premium)' : '#FFFFFF'
const CRIMSON = '#B10C0C'
const GREEN = 'var(--ge-primary)'
const GOLD = PBCC_PREMIUM ? 'var(--ge-gold)' : '#FFFFFF'
const GRAY = '#9CA3AF'
// 4-Ball brand has NO red — the white boxes get a thin dark-green outline
// instead of the crimson one. Full builds keep the PBCC crimson.
// TEST/premium: no light-green outline on the cream boxes — a thin GOLD hairline
// so they sit cleanly on the deep green. Club/live unchanged.
const BOX_BORDER = PBCC_PREMIUM ? GOLD : (FOURBALL_ONLY ? GREEN : CRIMSON)

const VB_W = 600
const VB_H = 450
const BH = 34          // tall cream boxes so names read across the room
// TEST: smaller box text so full team names fit inside the boxes with no
// overflow/clipping (the 17px serif ran past the ~172px Day-1 box). Club keeps
// its original sizes.
const NAME_FS = PBCC_PREMIUM ? 13 : 17
const PH_FS = PBCC_PREMIUM ? 12 : 15
const PG = 4

// Wide boxes, tight connector gaps — the cream dominates each column.
const D1_X = 8, D1_W = 194
const D2_X = 226, D2_W = 168
const D3_X = 424, D3_W = 168
const D2_CX = D2_X + D2_W / 2

export default function TvFlightBracket({ flight, bracket }) {
  const teams = flight?.teams || {}
  const rounds = bracket?.rounds || {}
  const name = (tid) => (tid ? (lastNames(teams[tid]?.info?.name) || null) : null)
  const hasScore = (tid) => !!(tid && teams[tid]?.scores && Object.keys(teams[tid].scores).length > 0)
  const liveOf = (m) => !!(m && m.a && m.b && !m.winnerId && (hasScore(m.a) || hasScore(m.b)))
  const firstMatch = (roundKey) => Object.values(rounds[roundKey]?.matches || {})[0] || null

  const r1 = rounds.r1?.matches || {}
  const SEED_ORDER = ['m1', 'm4', 'm3', 'm2'] // seeded 1v8, 4v5, 3v6, 2v7 top-to-bottom
  const day1 = SEED_ORDER.map((k) => ({ m: r1[k] || {}, live: liveOf(r1[k] || {}) }))

  const CONTENT_TOP = 58
  const CONTENT_BOT = 444
  const span = CONTENT_BOT - CONTENT_TOP
  const mCenter = (i) => CONTENT_TOP + (span * (i + 0.5)) / 4
  const pairTop = (c) => c - (BH + PG / 2)
  const midR = (yTop) => yTop + BH / 2

  const winnerName = (m) => (m?.winnerId ? name(m.winnerId) : null)
  const loserName = (m) => name(m?.winnerId ? (m.winnerId === m.a ? m.b : m.a) : null)

  const boxes = []
  const conns = []
  const pushBox = (x, y, w, text, color, placeholder) => boxes.push({ x, y, w, text, color, placeholder })

  // ---- DAY 1 ----
  day1.forEach((d, i) => {
    const c = mCenter(i), yTop = pairTop(c), y0 = yTop, y1 = yTop + BH + PG
    const aName = name(d.m.a), bName = name(d.m.b)
    const col = (nm) => (!nm ? GRAY : CRIMSON)
    pushBox(D1_X, y0, D1_W, aName || '—', col(aName), !aName)
    pushBox(D1_X, y1, D1_W, bName || '—', col(bName), !bName)
    const xR = D1_X + D1_W, xOut = xR + 14
    conns.push(`M${xR} ${midR(y0)} L${xOut} ${midR(y0)} L${xOut} ${midR(y1)} L${xR} ${midR(y1)}`)
    conns.push(`M${xOut} ${c} L${xOut + 12} ${c}`)
  })

  // ---- DAY 2 — Winners (align to matchups 1&2), Consolation (3&4) ----
  const winPairC = [mCenter(0), mCenter(1)]
  const conPairC = [mCenter(2), mCenter(3)]
  const wSlots = day1.map((d, i) => ({ m: d.m, label: `W${i + 1}` }))
  const lSlots = day1.map((d, i) => ({ m: d.m, label: `L${i + 1}` }))
  ;[[0, 1], [2, 3]].forEach((pair, pi) => {
    const c = winPairC[pi], yTop = pairTop(c)
    pair.forEach((si, k) => {
      const y = yTop + k * (BH + PG), s = wSlots[si], nm = winnerName(s.m)
      pushBox(D2_X, y, D2_W, nm || s.label, CRIMSON, !nm)
    })
    const y0 = yTop, y1 = yTop + BH + PG, xR = D2_X + D2_W, xOut = xR + 14
    conns.push(`M${xR} ${midR(y0)} L${xOut} ${midR(y0)} L${xOut} ${midR(y1)} L${xR} ${midR(y1)}`)
    conns.push(`M${xOut} ${c} L${xOut + 12} ${c}`)
  })
  ;[[0, 1], [2, 3]].forEach((pair, pi) => {
    const c = conPairC[pi], yTop = pairTop(c)
    pair.forEach((si, k) => {
      const y = yTop + k * (BH + PG), s = lSlots[si], nm = loserName(s.m)
      pushBox(D2_X, y, D2_W, nm || s.label, CRIMSON, !nm)
    })
  })

  // ---- DAY 3 — four result sections, each aligned to a Round-1 row ----
  const d3 = [
    { header: 'CHAMPIONSHIP', m: firstMatch('champ'), aL: 'W5', bL: 'W6' },
    { header: '3RD PLACE', m: firstMatch('third'), aL: 'L5', bL: 'L6' },
    { header: 'CONSOLATION FINAL', m: firstMatch('consf'), aL: 'L7', bL: 'L8' },
    { header: '5TH PLACE', m: firstMatch('fifth'), aL: 'L9', bL: 'L10' },
  ]
  const d3Headers = []
  d3.forEach((s, i) => {
    const c = mCenter(i), yTop = pairTop(c), y0 = yTop, y1 = yTop + BH + PG
    // CHAMPIONSHIP is the top-of-column label — sit it at the same height as
    // WINNERS BRACKET (y=44); the rest label the gap above their pair.
    d3Headers.push({ y: i === 0 ? 44 : yTop - 8, text: s.header })
    const aNm = name(s.m?.a), bNm = name(s.m?.b)
    pushBox(D3_X, y0, D3_W, aNm || s.aL, CRIMSON, !aNm)
    pushBox(D3_X, y1, D3_W, bNm || s.bL, CRIMSON, !bNm)
  })

  const trunc = (s) => (s && s.length > 22 ? s.slice(0, 20) + '…' : s)
  const D1_CX = D1_X + D1_W / 2
  const D3_CX = D3_X + D3_W / 2

  // Day-column header pills + gold section labels, collected so the HTML render
  // can position them by the same 600×450 coordinates the boxes use.
  const dayHeaders = [['DAY 1', D1_CX], ['DAY 2', D2_CX], ['DAY 3', D3_CX]]
  const goldLabels = [
    { text: 'ROUND 1', cx: D1_CX, y: 44 },
    { text: 'WINNERS BRACKET', cx: D2_CX, y: 44 },
    { text: 'CONSOLATION BRACKET', cx: D2_CX, y: pairTop(conPairC[0]) - 8 },
    ...d3Headers.map((h) => ({ text: h.text, cx: D3_CX, y: h.y })),
  ]

  // ── Pure HTML/DOM render (NOT SVG) ──────────────────────────────────────
  // The Hisense (Vidaa) TV browser fails to paint SVG <text>, so the white
  // bracket boxes rendered EMPTY on that TV (names invisible). HTML text always
  // renders. We draw every box and label as an HTML div, measuring the tile's
  // design-space size (clientWidth/Height — NOT affected by TvStage's transform)
  // and uniformly scaling the fixed 600×450 layout to fit (same letterboxing as
  // SVG preserveAspectRatio="meet"), so positions and font sizes scale exactly
  // as before. Everything is solid: white boxes (#FFFFFF), black text (#000000).
  const wrapRef = useRef(null)
  const [dim, setDim] = useState({ w: 0, h: 0 })
  useLayoutEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const measure = () => setDim({ w: el.clientWidth, h: el.clientHeight })
    measure()
    let ro
    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(measure); ro.observe(el) }
    window.addEventListener('resize', measure)
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', measure) }
  }, [])
  const s = dim.w > 0 && dim.h > 0 ? Math.min(dim.w / VB_W, dim.h / VB_H) : 0
  const ox = (dim.w - VB_W * s) / 2
  const oy = (dim.h - VB_H * s) / 2
  const px = (x) => ox + x * s
  const py = (y) => oy + y * s
  const SERIF = 'Georgia, "Times New Roman", serif'
  const bw = Math.max(1, 1.5 * s) // border stays visible even when scaled down

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
      {s > 0 && (
        <>
          {/* Match + result boxes — solid white, solid BLACK text */}
          {boxes.map((b, i) => (
            <div key={`box-${i}`} style={{
              position: 'absolute', left: px(b.x), top: py(b.y), width: b.w * s, height: BH * s,
              backgroundColor: CREAM, opacity: 1, colorScheme: 'light',
              border: `${bw}px solid ${BOX_BORDER}`, borderRadius: 12 * s,
              display: 'flex', alignItems: 'center', justifyContent: b.placeholder ? 'center' : 'flex-start',
              padding: b.placeholder ? 0 : `0 ${11 * s}px`, boxSizing: 'border-box', overflow: 'hidden',
              color: '#000000', fontFamily: SERIF, fontWeight: b.placeholder ? 800 : 700,
              fontSize: (b.placeholder ? PH_FS : NAME_FS) * s, lineHeight: 1, whiteSpace: 'nowrap',
              letterSpacing: b.placeholder ? `${0.06 * 15 * s}px` : 'normal',
            }}>
              {b.placeholder ? b.text : trunc(b.text)}
            </div>
          ))}
          {/* Day column header pills — solid white, black text */}
          {dayHeaders.map(([t, cx]) => (
            <div key={t} style={{
              position: 'absolute', left: px(cx - 48), top: py(5), width: 96 * s, height: 22 * s,
              backgroundColor: CREAM, opacity: 1, colorScheme: 'light',
              border: `${bw}px solid ${BOX_BORDER}`, borderRadius: 9 * s,
              display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box',
              color: '#000000', fontFamily: SERIF, fontWeight: 800, fontSize: 13 * s,
              letterSpacing: `${0.12 * 13 * s}px`, whiteSpace: 'nowrap', lineHeight: 1,
            }}>{t}</div>
          ))}
          {/* Section labels — gold, on the dark panel */}
          {goldLabels.map((g, i) => (
            <div key={`gl-${i}`} style={{
              position: 'absolute', left: px(g.cx - 130), top: py(g.y - 12), width: 260 * s, height: 16 * s,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: GOLD, fontFamily: SERIF, fontWeight: 700, fontSize: 12 * s,
              letterSpacing: `${0.1 * 12 * s}px`, whiteSpace: 'nowrap', lineHeight: 1,
            }}>{g.text}</div>
          ))}
        </>
      )}
    </div>
  )
}
